import { createContext, useContext, useState, useEffect, useCallback } from "react";

const LocaleContext = createContext(null);
const KEY = "aicom_locale";

// Danish is the source language and the key. Only English overrides are listed.
const EN = {
  // --- Chrome / nav ---
  "Idébank": "Idea Bank",
  "Idégenerator": "Idea Generator",
  "LinkedIn-opslag": "LinkedIn posts",
  "Facebook-opslag": "Facebook posts",
  "Nyhedsbrev": "Newsletter",
  "Presse": "Press",
  "Kampagner": "Campaigns",
  "Leadgenerator": "Lead generator",
  "Brandfortælling": "Brand story",
  "Ugens forslag": "This week's suggestions",
  "Vidensbank": "Knowledge base",
  "Gemte projekter": "Saved projects",
  "Indstillinger": "Settings",
  "Administrator": "Administrator",
  "Gratis": "Free",
  "Opgradér": "Upgrade",
  "Ubegrænset kreditter": "Unlimited credits",
  "{n} kredit tilbage": "{n} credit left",
  "{n} kreditter tilbage": "{n} credits left",
  "Log ud": "Log out",
  "AI har lært {n} % af din virksomhed": "AI has learned {n}% of your company",
  'Opgradér til {plan} for at låse "{label}" op': 'Upgrade to {plan} to unlock "{label}"',
  "Kræver {plan}-planen": "Requires the {plan} plan",
  "Sprog": "Language",
  "Dansk": "Danish",
  "Engelsk": "English",

  // --- common.jsx ---
  "Arbejder...": "Working...",
  "Kopiér": "Copy",
  "Kopieret til udklipsholder": "Copied to clipboard",
  "Gemt": "Saved",
  "Gem": "Save",

  // --- Dashboard ---
  "Godmorgen": "Good morning",
  "Goddag": "Good afternoon",
  "Godaften": "Good evening",
  "Vælg en funktion for at komme i gang med jeres kommunikation.": "Choose a feature to get started with your communication.",
  "10 skræddersyede idéer til hver kanal.": "10 tailored ideas for every channel.",
  "Færdige opslag, klar til at poste.": "Ready-made posts, ready to publish.",
  "Færdige opslag, tilpasset Facebook.": "Ready-made posts, tailored for Facebook.",
  "Emne, overskrift og brødtekst til nyhedsbrevet.": "Subject, headline and body for your newsletter.",
  "Vurdering af historien + færdig pressemeddelelse.": "Story assessment + finished press release.",
  "Færdige kampagnetekster til annoncer og landingsider.": "Ready-made campaign copy for ads and landing pages.",
  "Komplette lead magnets og guides — smukt brandet.": "Complete lead magnets and guides — beautifully branded.",
  "Jeres historie fortalt skarpt.": "Your story told sharply.",
  "Ugentlige forslag til hvad I bør kommunikere om.": "Weekly suggestions on what to communicate about.",
  "Upload dokumenter — så Aicom lærer jer endnu bedre at kende.": "Upload documents so Aicom gets to know you even better.",
  "Alle jeres gemte udkast ét sted.": "All your saved drafts in one place.",
  "Kræver {plan} — opgradér": "Requires {plan} — upgrade",

  // --- generic version/field labels ---
  "Professionel": "Professional",
  "Personlig": "Personal",
  "Salgsorienteret": "Sales-oriented",
  "Emnefelt": "Subject line",
  "Preview-tekst": "Preview text",
  "Overskrift": "Headline",
  "Indledning": "Introduction",
  "Brødtekst": "Body",
  "Call To Action": "Call To Action",
  "Kampagneidé": "Campaign idea",
  "Hovedbudskab": "Main message",
  "LinkedIn-annonce": "LinkedIn ad",
  "Meta-annonce": "Meta ad",
  "Google Ads-tekst": "Google Ads copy",
  "Landingpage-idé": "Landing page idea",

  // --- LinkedIn ---
  "Skriv et opslag, der bliver læst": "Write a post that gets read",
  "Skriv kort hvad det skal handle om — så får du tre versioner i jeres tone.": "Briefly describe the topic — you'll get three versions in your tone.",
  "Hvad skal opslaget handle om?": "What should the post be about?",
  "Skriv tre versioner": "Write three versions",
  "Skriv hvad opslaget skal handle om": "Describe what the post should be about",
  "Skriver jeres opslag...": "Writing your post...",
  "Tre versioner klar — gemt automatisk": "Three versions ready — saved automatically",
  "Gør det kortere": "Make it shorter",
  "Mere personligt": "More personal",
  "Tydeligere opfordring": "Clearer call to action",
  "Mere fagligt": "More professional",
  "Tilføj emojis": "Add emojis",

  // --- Facebook ---
  "Skriv et opslag, der skaber engagement": "Write a post that drives engagement",
  "Skriv kort hvad det skal handle om — så får du tre versioner, tilpasset Facebook.": "Briefly describe the topic — you'll get three versions tailored for Facebook.",
  "Mere uformelt": "More informal",

  // --- Newsletter ---
  "Et nyhedsbrev, folk åbner": "A newsletter people open",
  "Beskriv emnet kort — så bygger vi hele nyhedsbrevet.": "Briefly describe the topic — we'll build the whole newsletter.",
  "Hvad skal nyhedsbrevet handle om?": "What should the newsletter be about?",
  "Skriv nyhedsbrev": "Write newsletter",
  "Beskriv emnet for nyhedsbrevet": "Describe the newsletter topic",
  "Skriver jeres nyhedsbrev...": "Writing your newsletter...",
  "Nyhedsbrev klar — gemt automatisk": "Newsletter ready — saved automatically",
  "Kopiér hele nyhedsbrevet": "Copy the entire newsletter",
  "Kortere": "Shorter",
  "Mere fængende emnefelt": "More catchy subject line",
  "Stærkere CTA": "Stronger CTA",
  "Mindre corporate": "Less corporate",

  // --- Campaigns ---
  "Fra mål til færdig kampagne": "From goal to finished campaign",
  'Beskriv målet — fx "Vi vil have flere leads" — så bygger vi hele kampagnen.': 'Describe the goal — e.g. "We want more leads" — and we\'ll build the whole campaign.',
  "Hvad er kampagnens mål?": "What is the campaign's goal?",
  "Byg kampagne": "Build campaign",
  "Beskriv kampagnens mål": "Describe the campaign's goal",
  "Bygger jeres kampagne...": "Building your campaign...",
  "Kampagne klar — gemt automatisk": "Campaign ready — saved automatically",
  "Kopiér hele kampagnen": "Copy the entire campaign",
  "Gør det mere konkret": "Make it more concrete",
  "Skarpere hovedbudskab": "Sharper main message",
  "Mere salgsorienteret": "More sales-oriented",
  "Kortere annoncetekster": "Shorter ad copy",
  "Prøv en anden vinkel": "Try a different angle",

  // --- Brand ---
  "Fortæl jeres historie skarpt": "Tell your story sharply",
  "Beskriv virksomheden kort — eller lad feltet stå tomt og brug jeres eksisterende profil. Aicom AI skriver en konkret, levende brandfortælling uden floskler.": "Briefly describe the company — or leave the field empty and use your existing profile. Aicom AI writes a concrete, vivid brand story without clichés.",
  "Valgfrit: beskriv virksomheden kort (lad stå tomt for at bruge jeres profil)": "Optional: briefly describe the company (leave empty to use your profile)",
  "Skriv brandfortælling": "Write brand story",
  "Skriver jeres brandfortælling...": "Writing your brand story...",
  "Brandfortælling klar — gemt automatisk": "Brand story ready — saved automatically",
  "Kopiér alt": "Copy all",
  "Generér ny version": "Generate new version",
  "Gør den kortere": "Make it shorter",
  "Mere personlig": "More personal",
  "Fremhæv bæredygtighed": "Emphasize sustainability",
  "Brandfortælling": "Brand story",
  "Kort version": "Short version",
  "Elevator pitch": "Elevator pitch",
  "Kernebudskab": "Core message",
  "Tagline-forslag": "Tagline suggestions",
  "Har du ønsker til denne version? Skriv dem her — så retter Aicom AI fortællingen til.": "Any wishes for this version? Write them here — Aicom AI will adjust the story.",

  // --- Ideas ---
  "Idéer, når I mangler noget at sige": "Ideas when you're short of something to say",
  "Vælg en kanal, og få 10 skræddersyede idéer baseret på jeres profil.": "Choose a channel and get 10 tailored ideas based on your profile.",
  "Generér 10 idéer": "Generate 10 ideas",
  "Finder idéer til jer...": "Finding ideas for you...",
  "Ingen idéer endnu": "No ideas yet",
  "Vælg en kanal og generér.": "Choose a channel and generate.",
  "Skriv udkast": "Write draft",
  "Kampagne": "Campaign",

  // --- Press ---
  "Er historien værd at sende ud?": "Is the story worth sending out?",
  "Vi vurderer historien ud fra journalistiske nyhedskriterier — før vi skriver noget.": "We assess the story against journalistic news criteria — before writing anything.",
  'Fx "Vi har ansat en ny direktør" eller "Vi har udviklet et nyt produkt"': 'E.g. "We hired a new director" or "We developed a new product"',
  "Vurdér pressepotentiale": "Assess press potential",
  "Beskriv historien": "Describe the story",
  "Vurderer historien...": "Assessing the story...",
  "Samlet score": "Overall score",
  "Det taler for historien": "In favor of the story",
  "Det trækker ned": "What weakens it",
  "Sådan kan historien blive stærkere": "How the story can get stronger",
  "Relevante medier": "Relevant media",
  "Stærkeste vinkler": "Strongest angles",
  "Skriv pressemeddelelse": "Write press release",
  "Hjælp mig med at udvikle historien": "Help me develop the story",
  "Svar på disse spørgsmål, så gør vi historien stærkere": "Answer these questions and we'll make the story stronger",
  "Skriv dine svar her...": "Write your answers here...",
  "Skriv pressemeddelelse nu": "Write press release now",
  "Arbejder på historien...": "Working on the story...",
  "Pressemeddelelse klar — gemt automatisk": "Press release ready — saved automatically",
  "Gør den kortere ": "Make it shorter",
  "Fremhæv nyhedsværdien": "Emphasize the news value",
  "Mere citatvenlig": "More quote-friendly",
  "Neutral tone": "Neutral tone",
  "Skarpere rubrik": "Sharper headline",

  // --- Weekly ---
  "{greeting}, {name} 👋": "{greeting}, {name} 👋",
  "Her er, hvad Aicom AI anbefaler, at I kommunikerer om denne uge.": "Here's what Aicom AI recommends you communicate about this week.",
  "Dine kommunikationsforslag denne uge": "Your communication suggestions this week",
  "Nye forslag": "New suggestions",
  "Henter jeres ugentlige forslag...": "Loading your weekly suggestions...",
  "Lav udkast": "Create draft",
  "LinkedIn": "LinkedIn",
  "Idé": "Idea",
  "der": "there",

  // --- LanguageSelect (content) ---
  "På hvilket sprog skal indholdet være?": "In which language should the content be?",
  "På hvilket sprog skal materialet være?": "In which language should the material be?",

  // --- RefinePanel ---
  "Juster teksten": "Adjust the text",
  "Fortryd": "Undo",
  "Har du ønsker til denne version? Skriv dem her — så retter Aicom AI teksten til.": "Any wishes for this version? Write them here — Aicom AI will adjust the text.",
  "Retter teksten…": "Adjusting the text…",
  "Opdateret efter dit ønske.": "Updated as requested.",
  "Beklager — det gik ikke. Prøv igen.": "Sorry — that didn't work. Try again.",
  "Gik tilbage til forrige version.": "Reverted to the previous version.",
  "Skriv dit ønske til justering…": "Write your adjustment request…",
  "Send": "Send",

  // --- AttachmentUploader ---
  "Vedhæft dokument": "Attach document",
  "PDF, Word eller tekst – bruges som ekstra kontekst": "PDF, Word or text – used as extra context",
  "Jeg bekræfter, at det uploadede materiale ikke indeholder personfølsomme oplysninger.": "I confirm that the uploaded material does not contain sensitive personal data.",
  "Undlad at uploade følsomme personoplysninger (fx helbred, CPR-numre, race eller religion). Du er selv dataansvarlig for det, du uploader. Indhold behandles af vores AI-leverandør OpenAI og kan opbevares dér i op til 30 dage. Det bruges kun til at levere jeres egne resultater – aldrig til at træne tredjeparters AI-modeller.": "Do not upload sensitive personal data (e.g. health, national ID numbers, race or religion). You are the data controller for whatever you upload. Content is processed by our AI provider OpenAI and may be stored there for up to 30 days. It is used only to deliver your own results – never to train third-party AI models.",
  "Du kan vedhæfte op til {n} dokumenter": "You can attach up to {n} documents",

  // --- AssistantWidget ---
  "Hvad kan Aicom AI hjælpe mig med?": "What can Aicom AI help me with?",
  "Hvilket modul skal jeg starte med?": "Which module should I start with?",
  "Hvad er forskellen på planerne?": "What's the difference between the plans?",
  "Hvordan får jeg bedre indhold?": "How do I get better content?",
  "Aicom-hjælperen": "The Aicom Helper",
  "Spørg om alt på platformen": "Ask about anything on the platform",
  "Ryd samtale": "Clear conversation",
  "Luk": "Close",
  "Kræver Start-planen": "Requires the Start plan",
  "Opgradér og skriv": "Upgrade and write",
  "Aicom skriver en smagsprøve... (vent venligst)": "Aicom is writing a preview... (please wait)",
  "Opgradér for at få Aicom til at skrive jeres færdige brandfortælling med pitch, kernebudskab og taglines ud fra idéen.": "Upgrade to have Aicom write your finished brand story with pitch, core message and taglines from the idea.",
  "Opgradér for at få Aicom til at skrive et færdigt debatindlæg ud fra idéen.": "Upgrade to have Aicom write a finished op-ed from the idea.",
  "Opgradér for at få Aicom til at skrive færdige kampagnetekster til annoncer og landingssider ud fra idéen.": "Upgrade to have Aicom write finished campaign copy for ads and landing pages from the idea.",
  "Opgradér for at få Aicom til at bygge en færdig lead magnet med kampagne ud fra idéen.": "Upgrade to have Aicom build a finished lead magnet with a campaign from the idea.",
  "Smagsprøve": "Preview",
  "Start gratis prøve af {plan}": "Start free trial of {plan}",
  "Opgradér til {plan}": "Upgrade to {plan}",
  "{price} kr./md. inkl. moms efter 30 dages gratis prøveperiode. Der trækkes ikke betaling nu. Abonnementet fornyes automatisk, indtil det opsiges.": "DKK {price}/month incl. VAT after a 30-day free trial. You won't be charged now. The subscription renews automatically until cancelled.",
  "{price} kr./md. inkl. moms. Betalingen trækkes med det samme, og abonnementet fornyes automatisk hver måned, indtil det opsiges.": "DKK {price}/month incl. VAT. You are charged immediately and the subscription renews monthly until cancelled.",
  "Opgradér for at få Aicom til at skrive et færdigt nyhedsbrev ud fra idéen.": "Upgrade to have Aicom write a finished newsletter from the idea.",
  "Opgradér for at få Aicom til at vurdere historiens pressepotentiale og skrive en færdig pressemeddelelse ud fra idéen.": "Upgrade to have Aicom assess the story's press potential and write a finished press release from the idea.",
  "Hej! Hvad kan jeg hjælpe med?": "Hi! How can I help?",
  "Jeg kender platformen og jeres virksomhed.": "I know the platform and your company.",
  "Skriver...": "Typing...",
  "Skriv dit spørgsmål...": "Write your question...",
  "Beklager – jeg kunne ikke svare lige nu. Prøv igen om lidt.": "Sorry – I couldn't answer right now. Please try again shortly.",
  "Samtalen er ryddet": "Conversation cleared",

  // --- Projects ---
  "Alt jeres indhold ét sted": "All your content in one place",
  "Alle genererede tekster gemmes automatisk. Søg, åbn, redigér, kopiér eller slet.": "All generated texts are saved automatically. Search, open, edit, copy or delete.",
  "Søg i projekter...": "Search projects...",
  "Alle": "All",
  "Henter projekter...": "Loading projects...",
  "Ingen gemte projekter endnu": "No saved projects yet",
  "Alt du genererer, dukker op her.": "Everything you generate shows up here.",
  "Åbn": "Open",
  "Slettet": "Deleted",
  "Gem ændringer": "Save changes",

  // --- Knowledge ---
  "Gør Aicom AI klogere med jeres profil": "Make Aicom AI smarter with your profile",
  "Angiv branche, tone of voice, LinkedIn og mere — så skriver Aicom AI endnu mere præcist i jeres stil.": "Add industry, tone of voice, LinkedIn and more — so Aicom AI writes even more precisely in your style.",
  "Gem profil": "Save profile",
  "Virksomhedens centrale viden": "Your company's central knowledge",
  "Jo mere materiale du uploader, desto bedre bliver Aicom AI til at forstå virksomheden og skrive i dens stil.": "The more material you upload, the better Aicom AI understands your company and writes in its style.",
  "Gør Aicom AI endnu klogere på din virksomhed": "Make Aicom AI even smarter about your company",
  "Upload dokumenter, cases, præsentationer, produktark, pressemeddelelser, nyhedsbreve, brochurer og andet materiale. Aicom AI analyserer indholdet og bruger det aktivt til at skabe mere relevante og virksomhedsspecifikke kommunikationsforslag.": "Upload documents, cases, presentations, product sheets, press releases, newsletters, brochures and other material. Aicom AI analyzes the content and uses it actively to create more relevant, company-specific communication suggestions.",
  "Virksomhedskendskab": "Company knowledge",
  "Jo mere materiale du uploader, desto bedre bliver Aicom AI til at forstå din virksomhed og levere relevante forslag.": "The more material you upload, the better Aicom AI understands your company and delivers relevant suggestions.",
  "Uploader og analyserer…": "Uploading and analyzing…",
  "Slip filer her eller vælg filer": "Drop files here or choose files",
  "PDF • Word • PowerPoint • Excel • Billeder • Tekstfiler": "PDF • Word • PowerPoint • Excel • Images • Text files",
  "Upload dokument": "Upload document",
  "Eller skriv om virksomheden": "Or write about the company",
  "Har du ikke et dokument? Skriv bare fri tekst — fx om jeres historie, produkter, kunder eller værdier. Aicom AI bruger det aktivt.": "Don't have a document? Just write free text — e.g. about your history, products, customers or values. Aicom AI uses it actively.",
  "Titel (valgfri) — fx 'Vores historie'": "Title (optional) — e.g. 'Our story'",
  "Skriv tekst om virksomheden her...": "Write text about the company here...",
  "Gem tekst": "Save text",
  "Skriv noget tekst om virksomheden": "Write some text about the company",
  "Tekst gemt i vidensbanken": "Text saved to the knowledge base",
  "Videnskategorier": "Knowledge categories",
  "Brand": "Brand",
  "Produkter & ydelser": "Products & services",
  "Kommunikation": "Communication",
  "Salg & marketing": "Sales & marketing",
  "Organisation": "Organization",
  "{n} af 6": "{n} of 6",
  "{n} profiler": "{n} profiles",
  "{n} dokumenter": "{n} documents",
  "Aicom AI anbefaler": "Aicom AI recommends",
  "Seneste dokumenter": "Recent documents",
  "Henter dokumenter…": "Loading documents…",
  "Ingen dokumenter endnu. Upload jeres materiale ovenfor.": "No documents yet. Upload your material above.",
  "Dokument": "Document",
  "Kategori": "Category",
  "Uploadet": "Uploaded",
  "Status": "Status",
  "Handlinger": "Actions",
  "Omdøb": "Rename",
  "Flyt kategori": "Move category",
  "Slet": "Delete",
  "Omdøbt": "Renamed",
  "Flyttet": "Moved",
  "Alt bliver brugt aktivt": "Everything is used actively",
  "Aicom AI bruger automatisk materialet i Vidensbanken, når platformen udvikler kommunikationsforslag, LinkedIn-opslag, nyhedsbreve, pressehistorier og kampagner. Derfor bliver forslagene mere præcise, mere relevante og mere tro mod virksomhedens identitet, jo mere viden AI'en får adgang til.": "Aicom AI automatically uses the material in the Knowledge Base when developing communication suggestions, LinkedIn posts, newsletters, press stories and campaigns. That makes the suggestions more precise, more relevant and more true to your company's identity the more knowledge the AI has access to.",
  "Omdøb dokument": "Rename document",
  "Flyt til kategori": "Move to category",
  "Flyt": "Move",

  // --- Settings ---
  "Jeres virksomhedsprofil": "Your company profile",
  "Denne profil bruges i al fremtidig kommunikation. Hold den opdateret.": "This profile is used in all future communication. Keep it up to date.",
  "Virksomhedsnavn": "Company name",
  "Website": "Website",
  "Branche": "Industry",
  "Tone of voice": "Tone of voice",
  "Primær målgruppe": "Primary audience",
  "Beskrivelse": "Description",
  "Profil opdateret": "Profile updated",
  "AI-genereret profil": "AI-generated profile",
  "Branding": "Branding",
  "Logo og brandfarver bruges automatisk, når Aicom AI genererer PDF'er (fx lead magnets).": "Logo and brand colors are used automatically when Aicom AI generates PDFs (e.g. lead magnets).",
  "Upload logo": "Upload logo",
  "Fjern": "Remove",
  "Primær farve (forside)": "Primary color (cover)",
  "Accentfarve": "Accent color",
  "Gem branding": "Save branding",
  "Branding gemt": "Branding saved",
  "Logo uploadet": "Logo uploaded",
  "Logo fjernet": "Logo removed",
  "Konto": "Account",
  "Udseende": "Appearance",
  "Mørkt": "Dark",
  "Lyst": "Light",
  "Vælg, hvordan appen skal se ud. Dit valg gemmes på denne enhed.": "Choose how the app looks. Your choice is saved on this device.",
  "Abonnement": "Subscription",
  "{used} kreditter brugt af {limit}": "{used} credits used of {limit}",
  "{used} kreditter brugt · ubegrænset": "{used} credits used · unlimited",
  "Abonnementet er opsagt og udløber {date}.": "The subscription is canceled and expires {date}.",
  "Fornyes automatisk {date}.": "Renews automatically {date}.",
  "Åbner...": "Opening...",
  "Administrér abonnement": "Manage subscription",
  "Skift plan": "Change plan",
  "Se planer": "See plans",
  "Abonnementet styres af virksomhedens ejer.": "The subscription is managed by the company owner.",

  // --- Onboarding ---
  "Analyserer jeres virksomhed": "Analyzing your company",
  "Kom hurtigt i gang": "Get started fast",
  "Ingen hjemmeside?": "No website?",
  "Beskriv kort jeres virksomhed her — hvad I laver, for hvem, og hvad der gør jer særlige.": "Briefly describe your company here — what you do, for whom, and what makes you special.",
  "Fx: Vi er et arkitektfirma i Aarhus, der designer bæredygtige boliger for private bygherrer …": "E.g.: We're an architecture firm in Aarhus designing sustainable homes for private clients …",
  "Vi gennemgår jeres hjemmeside og bygger en profil, der bruges i al fremtidig kommunikation.": "We review your website and build a profile used in all future communication.",
  "Fortæl os om jer": "Tell us about you",
  "Du behøver kun gøre det én gang. Herefter kender Aicom jeres virksomhed.": "You only need to do this once. After that Aicom knows your company.",
  "Fx jeres virksomhedsnavn": "E.g. your company name",
  "Vælg branche": "Choose industry",
  "Kort beskrivelse af virksomheden": "Short description of the company",
  "Hvad laver I, og hvad gør jer særlige?": "What do you do, and what makes you special?",
  "Fx arkitekter og bygherrer": "E.g. architects and developers",
  "LinkedIn-side eller -profil": "LinkedIn page or profile",
  "Ønsket tone of voice": "Desired tone of voice",
  "Vælg tone": "Choose tone",
  "Byg vores profil": "Build our profile",
  "Jeres profil er bygget": "Your profile is built",
  "Kunne ikke bygge profilen": "Could not build the profile",

  // --- misc ---
  "Nye forslag klar": "New suggestions ready",
  "Tysk": "German",
  "Svensk": "Swedish",
  "Norsk": "Norwegian",

  // --- Lead ---
  "Fra idé til komplet leadkampagne": "From idea to a complete lead campaign",
  "Aicom AI hjælper jer med at bygge en professionel lead magnet — og hele kampagnen omkring den.": "Aicom AI helps you build a professional lead magnet — and the whole campaign around it.",
  "Kurser": "Courses",
  "Konsulentydelser": "Consulting services",
  "Software": "Software",
  "Abonnementer": "Subscriptions",
  "Produkter": "Products",
  "Events": "Events",
  "Booke et møde": "Book a meeting",
  "Bestille en demo": "Book a demo",
  "Kontakte virksomheden": "Contact the company",
  "Tilmelde sig nyhedsbrevet": "Sign up for the newsletter",
  "Købe et produkt": "Buy a product",
  "Bygger jeres lead magnet…": "Building your lead magnet…",
  "Aicom AI skriver hele materialet — titel, kapitler, tips og call to action.": "Aicom AI writes the entire material — title, chapters, tips and call to action.",
  "Hvad ønsker I at sælge?": "What do you want to sell?",
  "Vælg det, der passer bedst — eller skriv jeres eget.": "Choose what fits best — or write your own.",
  "Eller skriv selv…": "Or write your own…",
  "Næste": "Next",
  "Hvem er målgruppen?": "Who is the audience?",
  "Fx jeres typiske kunder": "E.g. your typical customers",
  "Hvad er målgruppens største udfordring?": "What is the audience's biggest challenge?",
  "Fx den vigtigste udfordring jeres målgruppe står med": "E.g. the main challenge your audience faces",
  "Hvad ønsker I, at læseren skal gøre bagefter?": "What do you want the reader to do afterwards?",
  "Tilbage": "Back",
  "Find idéer": "Find ideas",
  "Vælg den idé, vi skal bygge": "Choose the idea we should build",
  "Rammer:": "Targets:",
  "Leadværdi:": "Lead value:",
  "Produktion:": "Production:",
  "Generér lead magnet": "Generate lead magnet",
  "Download PDF": "Download PDF",
  "Duplikér": "Duplicate",
  "Start forfra": "Start over",
  "Gør sproget enklere": "Simplify the language",
  "Mere konkret og handlingsorienteret": "More concrete and action-oriented",
  "Skarpere titel og intro": "Sharper title and intro",
  "Tilføj flere konkrete eksempler": "Add more concrete examples",
  "Lead magnet": "Lead magnet",
  "Introduktion": "Introduction",
  "Opsummering": "Summary",
  "Tilføj kapitel": "Add chapter",
  "Vil du også have resten af kampagnen?": "Do you also want the rest of the campaign?",
  "Aicom AI bygger landingpage, takkeside, LinkedIn-opslag, nyhedsbrev, Google & Meta Ads, CTA'er og en opfølgningsmail.": "Aicom AI builds a landing page, thank-you page, LinkedIn posts, newsletter, Google & Meta Ads, CTAs and a follow-up email.",
  "Ja, byg hele kampagnen": "Yes, build the whole campaign",
  "Din komplette kampagne": "Your complete campaign",
  "Landingpage": "Landing page",
  "Takkeside": "Thank-you page",
  "Google Ads": "Google Ads",
  "Meta Ads": "Meta Ads",
  "Call To Actions": "Call To Actions",
  "Opfølgningsmail": "Follow-up email",
  "Emne:": "Subject:",
  "Preview:": "Preview:",
  "Lead magnet klar — gemt automatisk": "Lead magnet ready — saved automatically",
  "Hele kampagnen er klar": "The whole campaign is ready",
  "Ændringer gemt": "Changes saved",
  "Projekt duplikeret": "Project duplicated",

  // --- TeamPanel ---
  "{used} af {seats} pladser brugt": "{used} of {seats} seats used",
  "Tilføj kollegaer, så I deler virksomhedsprofil, vidensbank og projekter.": "Add colleagues to share your company profile, knowledge base and projects.",
  "Jeres nuværende plan har plads til én bruger. Opgradér til Business for op til 5 brugere.": "Your current plan has room for one user. Upgrade to Business for up to 5 users.",
  "Ejer": "Owner",
  "Nulstil adgangskode": "Reset password",
  "Ingen ledige pladser": "No available seats",
  "Tilføj bruger": "Add user",
  "Tilføj teammedlem": "Add team member",
  "Opret en bruger med en midlertidig adgangskode, du deler med kollegaen.": "Create a user with a temporary password you share with your colleague.",
  "Navn": "Name",
  "E-mail": "Email",
  "Midlertidig adgangskode": "Temporary password",
  "Mindst 8 tegn": "At least 8 characters",
  "Del adgangskoden med brugeren. De kan logge ind med det samme.": "Share the password with the user. They can log in immediately.",
  "Annullér": "Cancel",
  "Opret bruger": "Create user",
  "Ny adgangskode": "New password",
  "Udfyld navn, e-mail og en adgangskode på mindst 8 tegn": "Fill in name, email and a password of at least 8 characters",
  "Bruger fjernet": "User removed",
  "Adgangskode nulstillet": "Password reset",
  "Angiv en ny midlertidig adgangskode, som brugeren kan logge ind med.": "Set a new temporary password the user can log in with.",
  "{name} er oprettet": "{name} created",
  "Fjern {name} fra teamet?": "Remove {name} from the team?",
  "Nulstil adgangskode for {name}": "Reset password for {name}",

  // --- Debate (Debatindlæg) ---
  "Debatindlæg": "Op-ed",
  "Skarpe, overbevisende debatindlæg der sætter dagsordenen.": "Sharp, persuasive op-eds that set the agenda.",
  "Sæt dagsordenen med et skarpt debatindlæg": "Set the agenda with a sharp op-ed",
  "Beskriv emnet og jeres holdning — så får du et gennemarbejdet, overbevisende debatindlæg i jeres tone.": "Describe the topic and your stance — you'll get a polished, persuasive op-ed in your tone.",
  "Skriv hvad debatindlægget skal handle om": "Describe what the op-ed should be about",
  "Dit debatindlæg er klar — gemt automatisk": "Your op-ed is ready — saved automatically",
  "Hvad skal debatindlægget handle om — og hvad er jeres holdning?": "What should the op-ed be about — and what is your stance?",
  "Skriv debatindlæg": "Write op-ed",
  "Skriver jeres debatindlæg...": "Writing your op-ed...",
  "Gør det skarpere": "Make it sharper",
  "Mere sagligt": "More factual",
  "Stærkere afslutning": "Stronger ending",
  "Tilføj et modargument": "Add a counterargument",

  // --- Pricing (/priser) ---
  "Vælg jeres plan": "Choose your plan",
  "Vælg din plan": "Choose your plan",
  "Start gratis. Opgradér når I er klar til mere. Priser i DKK inkl. moms.": "Start free. Upgrade when you're ready for more. Prices in DKK incl. VAT.",
  "Prøv Aicom af": "Try Aicom out",
  "Til solo & mindre virksomheder": "For solo & smaller businesses",
  "Den fulde kommunikationsrådgiver": "The full communications advisor",
  "Til bureauer & teams": "For agencies & teams",
  "50 kreditter": "50 credits",
  "10 kreditter": "10 credits",
  "10 kreditter (svarende til ca. 10 opslag om måneden)": "10 credits (about 10 posts per month)",
  "1000 kreditter": "1,000 credits",
  "Begrænset brug (50 kreditter)": "Limited use (50 credits)",
  "Begrænset brug (10 kreditter)": "Limited use (10 credits)",
  "Udvidet brug (1000 kreditter)": "Extended use (1,000 credits)",
  "Begrænset brug": "Limited use",
  "Udvidet brug": "Extended use",
  "Ubegrænset brug": "Unlimited use",
  "1 bruger - 1 virksomhed": "1 user - 1 company",
  "5 brugere - 1 virksomhed": "5 users - 1 company",
  "Din nuværende plan": "Your current plan",
  "Vælg Start": "Choose Start",
  "Vælg Pro": "Choose Pro",
  "Vælg Business": "Choose Business",
  "Køb Start": "Buy Start",
  "Køb Pro": "Buy Pro",
  "Køb Business": "Buy Business",
  "Køb {plan}": "Buy {plan}",
  "Vælg jeres plan": "Choose your plan",
  "Opgradér når som helst. Priser i DKK inkl. moms.": "Upgrade anytime. Prices in DKK incl. VAT.",
  "Start med 30 dages gratis prøve. Priser i DKK inkl. moms.": "Start with a 30-day free trial. Prices in DKK incl. VAT.",
  "Start": "Start",
  "Pro": "Pro",
  "Business": "Business",
  "Anbefalet": "Recommended",
  "inkl. moms": "incl. VAT",
  "14 dage gratis prøveperiode": "30-day free trial",
  "Eller prøv 14 dage gratis": "Or try 30 days free",
  "30 dage gratis prøveperiode": "30-day free trial",
  "Eller prøv 30 dage gratis": "Or try 30 days free",
  "Vælg dit abonnement": "Choose your subscription",
  "Vælg den løsning, der passer bedst til jeres virksomhed": "Choose the solution that best fits your company",
  "Alle planer og funktioner": "All plans and features",
  "Sammenlign pakkerne og vælg den, der passer bedst til jer": "Compare the plans and choose the one that fits you best",
  "30 dages gratis": "30 days free",
  "Derefter {price}/md. inkl. moms": "Then {price}/mo. incl. VAT",
  "Ingen tidsbegrænsning": "No time limit",
  "Prøv gratis i 30 dage": "Try 30 days free",
  "Fortsæt": "Continue",
  "Se alle planer og funktioner": "See all plans and features",
  "Start gratis prøve af {plan}": "Start a free trial of {plan}",
  "Nuværende plan": "Current plan",
  "Enterprise": "Enterprise",
  "Flere virksomheder, dedikeret onboarding og skræddersyede løsninger.": "Multiple companies, dedicated onboarding and tailored solutions.",
  "Kontakt os": "Contact us",
  "Skift til {plan}": "Switch to {plan}",
  "Start gratis prøve af {plan}": "Start a free trial of {plan}",
  "Bekræft køb af {plan}": "Confirm purchase of {plan}",
  "Dit nuværende abonnement ændres til denne plan. Der oprettes ikke et nyt abonnement, og betalingen justeres automatisk forholdsmæssigt.": "Your current subscription changes to this plan. No new subscription is created, and the payment is automatically prorated.",
  "Du starter med 14 dages gratis prøveperiode. Der trækkes ikke betaling nu — først når prøveperioden udløber, hvorefter abonnementet fornyes automatisk, indtil det opsiges. Dit betalingskort indtastes ved tilmelding.": "You start with a 30-day free trial. No payment is taken now — only when the trial ends, after which the subscription renews automatically until canceled. Your payment card is entered at sign-up.",
  "Du starter med 30 dages gratis prøveperiode. Der trækkes ikke betaling nu — først når prøveperioden udløber, hvorefter abonnementet fornyes automatisk, indtil det opsiges. Dit betalingskort indtastes ved tilmelding.": "You start with a 30-day free trial. No payment is taken now — only when the trial ends, after which the subscription renews automatically until canceled. Your payment card is entered at sign-up.",
  "Du køber abonnementet nu, og betalingen trækkes med det samme. Abonnementet fornyes automatisk hver måned, indtil det opsiges.": "You purchase the subscription now, and payment is taken immediately. The subscription renews automatically every month until canceled.",
  "Jeg har læst og accepterer": "I have read and accept the",
  "handelsbetingelserne": "terms and conditions",
  "Jeg accepterer, at levering af den digitale tjeneste påbegyndes med det samme, og at min eventuelle fortrydelsesret bortfalder i det omfang, dette følger af gældende regler om digitale tjenester.": "I accept that delivery of the digital service begins immediately, and that my right of withdrawal lapses to the extent this follows from the applicable rules on digital services.",
  "Fortsæt til betaling": "Continue to payment",
  "Start gratis prøve": "Start free trial",
  "Bekræft planskift": "Confirm plan change",
  "Din plan er ændret til {plan}. Betalingen justeres automatisk forholdsmæssigt.": "Your plan has been changed to {plan}. The payment is automatically prorated.",
};

const TRANSLATIONS = { da: {}, en: EN };

function interpolate(str, params) {
  if (!params) return str;
  return String(str).replace(/\{(\w+)\}/g, (_, k) => (params[k] != null ? params[k] : `{${k}}`));
}

export function LocaleProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    try { return localStorage.getItem(KEY) || "da"; } catch { return "da"; }
  });

  useEffect(() => {
    try { localStorage.setItem(KEY, locale); } catch (e) { void e; }
    try { document.documentElement.lang = locale; } catch (e) { void e; }
  }, [locale]);

  const setLocale = (l) => setLocaleState(l === "en" ? "en" : "da");
  const toggleLocale = () => setLocaleState((l) => (l === "da" ? "en" : "da"));

  const t = useCallback((key, params) => {
    const dict = TRANSLATIONS[locale] || {};
    const str = dict[key] != null ? dict[key] : key;
    return interpolate(str, params);
  }, [locale]);

  // Default content-generation language derived from interface locale
  const contentLang = locale === "en" ? "Engelsk" : "Dansk";

  return (
    <LocaleContext.Provider value={{ locale, setLocale, toggleLocale, t, contentLang }}>
      {children}
    </LocaleContext.Provider>
  );
}

export const useLocale = () => useContext(LocaleContext);
